import React, { Component, Fragment } from 'react';
import styles from './AdminTable.module.css';
import { message } from 'antd';
import { Table, Icon, Tag, Form, Select, Input, Popconfirm, Modal } from 'antd';

const { Item } = Form;
const { Option } = Select;
const EditableContext = React.createContext();
const { Provider, Consumer } = EditableContext;

const EditableRow = ({ form, index, ...props }) => {
  return (
    // we are writing the form object passed as
    // props from the Form.create() method antd gives us
    <Provider value={form}>
      <tr {...props} />
    </Provider>
  );
};

const EditableFormRow = Form.create()(EditableRow);

class EditableCell extends Component {
  selectAdminHandler = value => {
    console.log('selected: ', value);
  };

  // renderInput is to decipher which type of input we want to pass in
  renderInput = () => {
    const { inputType } = this.props;
    if (inputType === 'select')
      return (
        <Select initialValue="SALES" style={{ width: 100 }} onChange={this.selectAdminHandler}>
          <Option value="SALES">Sales</Option>
          <Option value="STOCK">Stock</Option>
        </Select>
      );
    return <Input />;
  };

  // renderForm is rendering each cell in the table as a form
  renderForm = form => {
    const { getFieldDecorator } = form;
    const { editing, dataIndex, title, inputType, record, index, ...props } = this.props;

    const rules =
      inputType === 'text'
        ? [
            { type: 'email', message: 'The input is not valid E-mail!' },
            { required: true, message: 'Please input your E-mail!' },
          ]
        : [];

    return (
      <td {...props}>
        {editing ? (
          <Item style={{ margin: 0 }}>
            {getFieldDecorator(dataIndex, {
              rules: rules,
              initialValue: record[dataIndex],
            })(this.renderInput())}
          </Item>
        ) : (
          props.children
        )}
      </td>
    );
  };

  render() {
    return (
      // recomsuming the passed in form object from previously initialized Provider
      <Consumer>{form => this.renderForm(form)}</Consumer>
    );
  }
}

class AdminTable extends Component {
  state = {
    editingID: 0,
    visible: false,
    adminID: 0,
  };

  toggleVisible = () => {
    this.setState(prev => ({ ...prev, visible: !this.state.visible }));
  };

  // render the type of role we wish to display
  renderRole = data => {
    if (data === 'STOCK') return <Tag color="green">STOCK</Tag>;
    else if (data === 'SALES') return <Tag color="magenta">SALES</Tag>;
  };

  // is editing is a handler to check if the current record is the same
  // as the local editingID of the table so as to figure out if the row is primed for editing
  isEditing = record => {
    return record.id === this.state.editingID;
  };

  // editHandler sets the local state to save the current id of the row thats being edited on
  editHandler = id => {
    this.setState(prev => ({ ...prev, editingID: id }));
  };

  // if the editingID is 0, it means that no rows are currently being edited upon
  cancelHandler = () => {
    this.setState(prev => ({ ...prev, editingID: 0 }));
  };

  // saveHandler handles the async call to server to store data
  saveHandler = (form, id) => {
    form.validateFields((error, row) => {
      if (error) return;
      this.props
        .updateAdmin(row, id)
        .then(() => {
          message.success('Update successful', 1);
          this.setState(prev => ({ ...prev, editingID: 0 }));
        })
        .catch(console.error);
    });
  };

  // renderSave renders the buttons to save and cancel out of an edit state
  renderSave = record => (
    <span>
      <Consumer>
        {form => (
          <a style={{ marginRight: 8 }} onClick={() => this.saveHandler(form, record.id)}>
            Save
          </a>
        )}
      </Consumer>
      <a onClick={() => this.cancelHandler()}>Cancel</a>
    </span>
  );

  // renderActions renderst the buttons to edit or delete during the non-edit state
  renderActions = record => (
    <Fragment>
      <a style={{ marginRight: 20 }}>
        <Icon type="edit" size="large" onClick={() => this.editHandler(record.id)} />
      </a>
      <a>
        <Popconfirm
          key="Delete"
          title="Confirm admin delete"
          okText="Yes"
          cancelText="No"
          onConfirm={() => {
            this.toggleVisible();
            this.setState(prev => ({ ...prev, adminID: record['id'] }));
          }}
        >
          <Icon type="delete" size="large" />
        </Popconfirm>
      </a>
    </Fragment>
  );

  renderTable = () => {
    const { admins, isLoading } = this.props;

    const components = { body: { row: EditableFormRow, cell: EditableCell } };
    const columns = [
      {
        title: 'Email',
        dataIndex: 'email',
        editable: true,
        width: '400px',
      },
      {
        title: 'Role',
        dataIndex: 'role',
        editable: true,
        width: '135px',
        render: row => this.renderRole(row),
      },
      {
        title: 'Last Logged In',
        dataIndex: 'last_login_at',
      },
      {
        title: 'Updated At',
        dataIndex: 'updated_at',
      },
      {
        title: 'Actions',
        dataIndex: 'actions',
        width: '130px',
        render: (_t, record) => {
          const editable = this.isEditing(record);
          return editable ? this.renderSave(record) : this.renderActions(record);
        },
      },
    ];

    // mapping through the columns on finding out which column is editable
    // and returning them with an onCell method
    // https://ant.design/components/table/#onCell
    // onCell sets the props of per cell which gets passed into
    // EditableCell class from the components props on <Table/>
    const columnsEditing = columns.map(col => {
      if (!col.editable) return col;
      return {
        ...col,
        onCell: record => {
          return {
            record,
            inputType: col.dataIndex === 'role' ? 'select' : 'text',
            dataIndex: col.dataIndex,
            title: col.title,
            editing: this.isEditing(record),
            width: col.dataIndex === 'role' ? '135px' : '400px',
          };
        },
      };
    });

    // components attribute
    // https://ant.design/components/table/#components
    // overrides the default table elements to provide extended capabilites
    // in this case, we want to pass in our EditableRow and EditableCell components
    // which are imbued with addition properties from the {form} object.
    return (
      <Table
        components={components}
        bordered
        loading={isLoading}
        className={styles.Table}
        rowKey={record => record.id}
        dataSource={admins}
        columns={columnsEditing}
      />
    );
  };

  render() {
    return (
      <Fragment>
        {this.renderTable()}
        <Modal
          title={<span>Warning!!</span>}
          visible={this.state.visible}
          width={300}
          okText="Yes I wish to continue"
          okType="danger"
          onCancel={this.toggleVisible}
          onOk={() => {
            console.log(this.state.adminID);
            this.props.deleteAdmin(this.state.adminID);
            this.toggleVisible();
          }}
        >
          <span>Deleting admin users will result in their related lists being deleted as well. </span>
          <br />
          <br />
          <span>Do you wish to continue?</span>
        </Modal>
      </Fragment>
    );
  }
}

export default AdminTable;



// WEBPACK FOOTER //
// ./src/components/Admin/SuperAdmin/Table/AdminTable.js